import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Download, Loader2, Users, TrendingUp, DollarSign, Award, ArrowUp, ArrowDown, ArrowUpDown,
  X, ChevronDown, Check, RotateCcw, Building2, Filter,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePlayers, usePlayerStats, usePlayerFacets, fetchAllPlayers } from '../hooks/usePlayersData';
import { AccountFacet, DealType, DealTypeFilter, Player, PlayerFilters, SortField, SortOrder } from '../types/players.types';
import { format } from 'date-fns';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(value || 0);

const formatDate = (value: string | null | undefined, pattern = 'MMM dd, yyyy') => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : format(d, pattern);
};

const countryFlag = (code: string | null | undefined) => {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  return String.fromCodePoint(...[...upper].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));
};

const DEAL_STYLES: Record<DealType, string> = {
  CPA: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  RevShare: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
  Hybrid: 'bg-violet-500/10 text-violet-300 border-violet-500/30',
};

const DealBadge: React.FC<{ dealType: DealType | null }> = ({ dealType }) => {
  if (!dealType) return <span className="text-xs text-zinc-600">—</span>;
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold tracking-wide ${DEAL_STYLES[dealType]}`}>
      {dealType === 'RevShare' ? 'REV SHARE' : dealType.toUpperCase()}
    </span>
  );
};

const PlatformBadge: React.FC<{ platform: string | null }> = ({ platform }) =>
  platform ? (
    <span className="inline-flex items-center rounded border border-[hsl(225,15%,22%)] bg-[hsl(225,15%,14%)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-400">
      {platform}
    </span>
  ) : null;

const TagChip: React.FC<{ label: string }> = ({ label }) => (
  <span className="inline-flex items-center rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-300/90">
    {label}
  </span>
);

const csvEscape = (v: unknown) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (players: Player[]) => {
  const header = [
    'Player ID', 'Account', 'Program', 'Brands', 'Platform', 'Tags', 'Deal Type', 'FTD', 'First Deposit',
    'Country', 'First Seen', 'Last Active', 'Deposits (EUR)', 'Deposits (#)', 'NGR (EUR)', 'Commission (EUR)', 'Status',
  ];
  const rows = players.map(p => [
    p.playerId, p.account, p.program, p.brands.join(' | '), p.platform || '', p.tags.join(' | '), p.dealType || '',
    p.isFtd ? 'yes' : 'no', p.firstDepositDate || '', p.country || '', p.firstSeen || '', p.lastActive || '',
    p.totalDeposits.toFixed(2), p.totalDepositsCount, p.ngr.toFixed(2), p.commission.toFixed(2), p.status,
  ]);
  const csv = [header, ...rows].map(r => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `players-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Small UI primitives
// ---------------------------------------------------------------------------

const controlBase =
  'h-10 rounded-lg border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] px-3 text-sm text-zinc-200 transition-all hover:border-[hsl(225,15%,28%)] focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20';

const Select: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  className?: string;
}> = ({ value, onChange, options, placeholder, className = '' }) => (
  <div className={`relative ${className}`}>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`${controlBase} w-full appearance-none pr-9 ${value ? 'border-emerald-500/40 text-white' : ''}`}
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
  </div>
);

const Toggle: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex h-10 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-all ${
      active
        ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
        : 'border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] text-zinc-400 hover:border-[hsl(225,15%,28%)] hover:text-zinc-200'
    }`}
  >
    {active && <Check className="h-3.5 w-3.5" />}
    {children}
  </button>
);

const Segmented: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}> = ({ value, onChange, options }) => (
  <div className="inline-flex h-10 items-center rounded-lg border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] p-1">
    {options.map(o => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`h-8 rounded-md px-3 text-xs font-semibold uppercase tracking-wide transition-all ${
          value === o.value ? 'bg-[hsl(225,15%,20%)] text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-200'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

/** Multi-select of accounts, grouped by program, with counts */
const AccountPicker: React.FC<{
  accounts: AccountFacet[];
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ accounts, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = accounts.filter(a =>
      !q ||
      a.account.toLowerCase().includes(q) ||
      a.program.toLowerCase().includes(q) ||
      a.brands.some(b => b.toLowerCase().includes(q)) ||
      (a.platform || '').toLowerCase().includes(q)
    );
    const map = new Map<string, AccountFacet[]>();
    for (const a of filtered) {
      const list = map.get(a.program) || [];
      list.push(a);
      map.set(a.program, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [accounts, query]);

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);

  const label =
    selected.length === 0
      ? 'All accounts'
      : selected.length === 1
        ? accounts.find(a => a.id === selected[0])?.account || '1 account'
        : `${selected.length} accounts`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`${controlBase} inline-flex min-w-[180px] items-center justify-between gap-2 ${selected.length ? 'border-emerald-500/40 text-white' : ''}`}
      >
        <span className="inline-flex items-center gap-2 truncate">
          <Building2 className="h-4 w-4 text-zinc-500" />
          {label}
        </span>
        <ChevronDown className="h-4 w-4 text-zinc-500" />
      </button>

      {open && (
        <div className="absolute left-0 z-30 mt-2 w-[380px] overflow-hidden rounded-xl border border-[hsl(225,15%,20%)] bg-[hsl(225,15%,9%)] shadow-2xl shadow-black/50">
          <div className="border-b border-[hsl(225,15%,15%)] p-2">
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Filter accounts, brands, platforms…"
              className="w-full rounded-lg border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,12%)] px-3 py-2 text-sm text-white placeholder-zinc-500 focus:border-emerald-500/50 focus:outline-none"
            />
          </div>
          <div className="max-h-[360px] overflow-y-auto p-1">
            {groups.length === 0 && <p className="px-3 py-6 text-center text-sm text-zinc-500">No accounts match</p>}
            {groups.map(([program, list]) => (
              <div key={program} className="mb-1">
                <div className="flex items-center justify-between px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  <span>{program}</span>
                  <PlatformBadge platform={list[0]?.platform || null} />
                </div>
                {list.map(a => {
                  const isSelected = selected.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggle(a.id)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors hover:bg-[hsl(225,15%,14%)] ${isSelected ? 'bg-emerald-500/5' : ''}`}
                    >
                      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${isSelected ? 'border-emerald-500 bg-emerald-500 text-black' : 'border-zinc-600'}`}>
                        {isSelected && <Check className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-zinc-100">{a.account}</span>
                        <span className="block truncate text-xs text-zinc-500">
                          {a.brands.join(', ')}
                          {a.tags.length > 0 && ` · ${a.tags.join(', ')}`}
                        </span>
                      </span>
                      <span className="shrink-0 text-right text-xs tabular-nums text-zinc-500">
                        <span className="block text-zinc-300">{a.players.toLocaleString()}</span>
                        <span className="block">{a.ftds.toLocaleString()} FTD</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          {selected.length > 0 && (
            <div className="flex items-center justify-between border-t border-[hsl(225,15%,15%)] px-3 py-2">
              <span className="text-xs text-zinc-500">{selected.length} selected</span>
              <button type="button" onClick={() => onChange([])} className="text-xs font-medium text-emerald-400 hover:text-emerald-300">
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const DEFAULT_FILTERS: PlayerFilters = {
  search: '',
  networkIds: [],
  status: [],
  commissionTiers: [],
  dealType: '',
  ftdOnly: false,
  withCommission: false,
  sortBy: 'commission',
  sortOrder: 'desc',
};

const NO_MATCH_SENTINEL = '__no_account_match__';

const Players: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [filters, setFilters] = useState<PlayerFilters>(DEFAULT_FILTERS);
  const [brand, setBrand] = useState('');
  const [platform, setPlatform] = useState('');
  const [exporting, setExporting] = useState(false);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: facets = [], isLoading: facetsLoading } = usePlayerFacets();

  const brandOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of facets) for (const b of a.brands) counts.set(b, (counts.get(b) || 0) + a.players);
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, label: `${value} (${count.toLocaleString()})` }));
  }, [facets]);

  const platformOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of facets) if (a.platform) counts.set(a.platform, (counts.get(a.platform) || 0) + a.players);
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, label: `${value} (${count.toLocaleString()})` }));
  }, [facets]);

  // Accounts narrowed by brand / platform - these feed the picker and the effective account filter
  const candidateAccounts = useMemo(
    () => facets.filter(a => (!brand || a.brands.includes(brand)) && (!platform || a.platform === platform)),
    [facets, brand, platform]
  );

  const effectiveAccountIds = useMemo(() => {
    const candidateIds = new Set(candidateAccounts.map(a => a.id));
    if (filters.networkIds.length > 0) {
      const ids = brand || platform ? filters.networkIds.filter(id => candidateIds.has(id)) : filters.networkIds;
      return ids.length > 0 ? ids : [NO_MATCH_SENTINEL];
    }
    if (brand || platform) return candidateIds.size > 0 ? Array.from(candidateIds) : [NO_MATCH_SENTINEL];
    return [];
  }, [candidateAccounts, filters.networkIds, brand, platform]);

  const queryFilters = useMemo<PlayerFilters>(
    () => ({ ...filters, search, networkIds: effectiveAccountIds }),
    [filters, search, effectiveAccountIds]
  );

  const { data: playersData, isLoading: playersLoading, isFetching, error } = usePlayers(queryFilters, currentPage, perPage);
  const { data: stats, isLoading: statsLoading } = usePlayerStats();

  const players = playersData?.players || [];
  const totalPlayers = playersData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalPlayers / perPage));

  const update = (patch: Partial<PlayerFilters>) => {
    setFilters(prev => ({ ...prev, ...patch }));
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    setFilters(prev => {
      const numeric: SortField[] = ['total_deposits', 'total_deposits_count', 'ngr', 'commission', 'ltv', 'first_seen', 'last_active', 'first_deposit', 'is_ftd'];
      const defaultOrder: SortOrder = numeric.includes(field) ? 'desc' : 'asc';
      const nextOrder: SortOrder = prev.sortBy === field ? (prev.sortOrder === 'asc' ? 'desc' : 'asc') : defaultOrder;
      return { ...prev, sortBy: field, sortOrder: nextOrder };
    });
    setCurrentPage(1);
  };

  const resetAll = () => {
    setFilters(DEFAULT_FILTERS);
    setBrand('');
    setPlatform('');
    setSearchInput('');
    setSearch('');
    setCurrentPage(1);
  };

  const activeChips: Array<{ key: string; label: string; onRemove: () => void }> = [];
  if (search) activeChips.push({ key: 'search', label: `Search: “${search}”`, onRemove: () => setSearchInput('') });
  if (brand) activeChips.push({ key: 'brand', label: `Brand: ${brand}`, onRemove: () => { setBrand(''); setCurrentPage(1); } });
  if (platform) activeChips.push({ key: 'platform', label: `Platform: ${platform}`, onRemove: () => { setPlatform(''); setCurrentPage(1); } });
  for (const id of filters.networkIds) {
    const acc = facets.find(a => a.id === id);
    activeChips.push({
      key: `acc-${id}`,
      label: `Account: ${acc?.account || id.slice(0, 8)}`,
      onRemove: () => update({ networkIds: filters.networkIds.filter(n => n !== id) }),
    });
  }
  if (filters.dealType) activeChips.push({ key: 'deal', label: `Deal: ${filters.dealType === 'revshare' ? 'Rev Share' : filters.dealType.toUpperCase()}`, onRemove: () => update({ dealType: '' }) });
  if (filters.ftdOnly) activeChips.push({ key: 'ftd', label: 'FTD only', onRemove: () => update({ ftdOnly: false }) });
  if (filters.withCommission) activeChips.push({ key: 'comm', label: 'With commission', onRemove: () => update({ withCommission: false }) });
  if (filters.status.length) activeChips.push({ key: 'status', label: `Status: ${filters.status[0]}`, onRemove: () => update({ status: [] }) });

  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = await fetchAllPlayers(queryFilters, 5000);
      if (rows.length === 0) {
        toast.info('Nothing to export for the current filters');
        return;
      }
      downloadCsv(rows);
      toast.success(`Exported ${rows.length.toLocaleString()} players${totalPlayers > rows.length ? ` (capped at ${rows.length.toLocaleString()} of ${totalPlayers.toLocaleString()})` : ''}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Accounts shown in the current result set (for the summary line)
  const accountsInView = useMemo(() => new Set(players.map(p => p.accountId)).size, [players]);

  const SortableHeader: React.FC<{ field: SortField; label: string; align?: 'left' | 'right' | 'center'; className?: string }> = ({
    field, label, align = 'left', className = '',
  }) => {
    const isActive = filters.sortBy === field;
    const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
    return (
      <th className={`px-4 py-3 text-xs font-medium uppercase tracking-wider text-zinc-500 ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} ${className}`}>
        <button
          type="button"
          onClick={() => handleSort(field)}
          className={`group inline-flex w-full items-center gap-1.5 ${justify} uppercase tracking-wider transition-colors hover:text-zinc-200 ${isActive ? 'text-zinc-100' : 'text-zinc-500'}`}
        >
          {label}
          {!isActive ? (
            <ArrowUpDown className="h-3.5 w-3.5 opacity-40 group-hover:opacity-100" />
          ) : filters.sortOrder === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 text-emerald-400" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 text-emerald-400" />
          )}
        </button>
      </th>
    );
  };

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Players</h1>
          <p className="mt-1 text-zinc-400">Every tracked player, with the brand, account and deal it belongs to</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || totalPlayers === 0}
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export {totalPlayers > 0 ? `(${Math.min(totalPlayers, 5000).toLocaleString()})` : ''}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statsLoading ? (
          <div className="col-span-4 flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : (
          <>
            {[
              { label: 'Total Players', value: stats?.totalPlayers?.toLocaleString() || '0', icon: Users, iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400', stagger: 'stagger-1' },
              { label: 'Active Players', value: stats?.activePlayers?.toLocaleString() || '0', icon: TrendingUp, iconBg: 'bg-blue-500/10', iconColor: 'text-blue-400', stagger: 'stagger-2' },
              { label: 'New This Month', value: stats?.newPlayersToday?.toLocaleString() || '0', icon: Award, iconBg: 'bg-purple-500/10', iconColor: 'text-purple-400', stagger: 'stagger-3' },
              { label: 'Total Commission', value: stats?.totalLTV || '€0.00', icon: DollarSign, iconBg: 'bg-amber-500/10', iconColor: 'text-amber-400', stagger: 'stagger-4' },
            ].map(card => (
              <div key={card.label} className={`rounded-xl border border-[hsl(225,15%,15%)] bg-[hsl(225,15%,10%)] p-5 animate-fade-in ${card.stagger}`}>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm text-zinc-400">{card.label}</p>
                  <div className={`rounded-lg ${card.iconBg} p-2`}>
                    <card.icon className={`h-4 w-4 ${card.iconColor}`} />
                  </div>
                </div>
                <p className="text-2xl font-bold tabular-nums text-white">{card.value}</p>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Filter bar */}
      <div className="mb-4 rounded-xl border border-[hsl(225,15%,15%)] bg-[hsl(225,15%,8%)] p-4 animate-fade-in">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[240px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search player ID, account, brand…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className={`${controlBase} w-full pl-9 pr-9 placeholder-zinc-500`}
            />
            {searchInput && (
              <button type="button" onClick={() => setSearchInput('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <AccountPicker
            accounts={candidateAccounts}
            selected={filters.networkIds}
            onChange={ids => update({ networkIds: ids })}
          />
          <Select value={brand} onChange={v => { setBrand(v); setCurrentPage(1); }} options={brandOptions} placeholder={facetsLoading ? 'Loading brands…' : 'All brands'} className="min-w-[170px]" />
          <Select value={platform} onChange={v => { setPlatform(v); setCurrentPage(1); }} options={platformOptions} placeholder="All platforms" className="min-w-[150px]" />
          <Select
            value={filters.status[0] || ''}
            onChange={v => update({ status: v ? [v as PlayerFilters['status'][number]] : [] })}
            options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'blocked', label: 'Blocked' }]}
            placeholder="Any status"
            className="min-w-[130px]"
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-zinc-500">
            <Filter className="h-3.5 w-3.5" /> Deal
          </div>
          <Segmented
            value={filters.dealType || ''}
            onChange={v => update({ dealType: v as DealTypeFilter })}
            options={[
              { value: '', label: 'All' },
              { value: 'cpa', label: 'CPA' },
              { value: 'revshare', label: 'Rev Share' },
              { value: 'hybrid', label: 'Hybrid' },
              { value: 'none', label: 'None' },
            ]}
          />
          <Toggle active={!!filters.ftdOnly} onClick={() => update({ ftdOnly: !filters.ftdOnly })}>FTD only</Toggle>
          <Toggle active={!!filters.withCommission} onClick={() => update({ withCommission: !filters.withCommission })}>With commission</Toggle>

          <div className="ml-auto flex items-center gap-3">
            <Select
              value={String(perPage)}
              onChange={v => { setPerPage(Number(v) || 25); setCurrentPage(1); }}
              options={[{ value: '25', label: '25 / page' }, { value: '50', label: '50 / page' }, { value: '100', label: '100 / page' }]}
              placeholder="25 / page"
              className="min-w-[120px]"
            />
            {activeChips.length > 0 && (
              <button type="button" onClick={resetAll} className="inline-flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm text-zinc-400 hover:text-white">
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </button>
            )}
          </div>
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[hsl(225,15%,13%)] pt-3">
            {activeChips.map(chip => (
              <span key={chip.key} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 py-1 pl-3 pr-1.5 text-xs text-emerald-200">
                {chip.label}
                <button type="button" onClick={chip.onRemove} className="rounded-full p-0.5 hover:bg-emerald-500/20">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Result summary */}
      <div className="mb-3 flex items-center justify-between text-sm text-zinc-500">
        <span>
          {playersLoading ? 'Loading…' : (
            <>
              <span className="font-medium text-zinc-200">{totalPlayers.toLocaleString()}</span> players
              {players.length > 0 && <> · <span className="text-zinc-300">{accountsInView}</span> account{accountsInView === 1 ? '' : 's'} on this page</>}
            </>
          )}
        </span>
        {isFetching && !playersLoading && <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[hsl(225,15%,15%)] bg-[hsl(225,15%,10%)] animate-fade-in">
        {playersLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-red-400">{(error as Error).message}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-[hsl(225,15%,15%)] bg-[hsl(225,15%,9%)]">
                  <SortableHeader field="player_id" label="Player" />
                  <SortableHeader field="network" label="Brand / Program" />
                  <SortableHeader field="account" label="Account" />
                  <SortableHeader field="deal_type" label="Deal" align="center" />
                  <SortableHeader field="first_deposit" label="FTD" />
                  <SortableHeader field="first_seen" label="First Seen" />
                  <SortableHeader field="last_active" label="Last Active" />
                  <SortableHeader field="total_deposits" label="Deposits" align="right" />
                  <SortableHeader field="ngr" label="NGR" align="right" />
                  <SortableHeader field="commission" label="Commission" align="right" />
                  <SortableHeader field="status" label="Status" align="center" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                {players.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Users className="h-12 w-12 text-zinc-600" />
                        <p className="text-zinc-500">
                          {activeChips.length > 0 ? 'No players match these filters.' : 'No players found. Sync your affiliate networks to start tracking.'}
                        </p>
                        {activeChips.length > 0 && (
                          <button type="button" onClick={resetAll} className="text-sm font-medium text-emerald-400 hover:text-emerald-300">Reset filters</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  players.map(player => {
                    const showProgram = player.brands.length === 0 || player.brands.join(',') !== player.program;
                    return (
                      <tr key={player.id} className={`transition-colors hover:bg-[hsl(225,15%,12%)] ${player.commission > 0 ? 'bg-emerald-500/[0.03]' : ''}`}>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-base leading-none" title={player.country || undefined}>{countryFlag(player.country)}</span>
                            <div>
                              <span className="block text-sm font-semibold tabular-nums text-white">{player.playerId}</span>
                              <span className="block text-[11px] text-zinc-500">{player.country || (player.email ? player.email : 'country n/a')}</span>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span className="block text-sm font-medium text-zinc-100">{player.brands.join(', ') || player.program}</span>
                          <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-zinc-500">
                            {showProgram && <span>{player.program}</span>}
                            <PlatformBadge platform={player.platform} />
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <button
                            type="button"
                            onClick={() => update({ networkIds: [player.accountId] })}
                            title="Filter by this account"
                            className="block text-left text-sm text-zinc-200 hover:text-emerald-300"
                          >
                            {player.account}
                          </button>
                          {player.tags.length > 0 && (
                            <span className="mt-0.5 flex gap-1">{player.tags.map(t => <TagChip key={t} label={t} />)}</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          <DealBadge dealType={player.dealType} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          {player.isFtd ? (
                            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-300">
                              <Check className="h-3.5 w-3.5" />
                              <span className="text-xs text-zinc-400">{player.firstDepositDate ? formatDate(player.firstDepositDate) : 'yes'}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-zinc-600">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-zinc-400">{formatDate(player.firstSeen)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-zinc-400">{formatDate(player.lastActive, 'MMM yyyy')}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <span className="block text-sm font-medium tabular-nums text-white">{formatCurrency(player.totalDeposits)}</span>
                          <span className="block text-[11px] tabular-nums text-zinc-500">{player.totalDepositsCount.toLocaleString()} deposit{player.totalDepositsCount === 1 ? '' : 's'}</span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <span className={`text-sm font-medium tabular-nums ${player.ngr > 0 ? 'text-emerald-400' : player.ngr < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                            {formatCurrency(player.ngr)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right">
                          <span className={`text-sm font-semibold tabular-nums ${player.commission > 0 ? 'text-amber-300' : 'text-zinc-600'}`}>
                            {formatCurrency(player.commission)}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-center">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                              player.status === 'active'
                                ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-400'
                                : player.status === 'inactive'
                                  ? 'border-zinc-500/20 bg-zinc-500/10 text-zinc-400'
                                  : 'border-red-500/20 bg-red-500/10 text-red-400'
                            }`}
                          >
                            {player.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPlayers > perPage && (
        <div className="mt-6 flex items-center justify-between animate-fade-in">
          <p className="text-sm text-zinc-500">
            Showing <span className="font-medium text-white">{(currentPage - 1) * perPage + 1}</span>–
            <span className="font-medium text-white">{Math.min(currentPage * perPage, totalPlayers)}</span> of{' '}
            <span className="font-medium text-white">{totalPlayers.toLocaleString()}</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="rounded-lg border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] px-3 py-2 text-sm text-zinc-400 transition-all hover:border-[hsl(225,15%,25%)] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-lg border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] px-4 py-2 text-sm font-medium text-zinc-400 transition-all hover:border-[hsl(225,15%,25%)] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <span className="px-2 text-sm tabular-nums text-zinc-400">
              Page <span className="text-white">{currentPage}</span> / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="rounded-lg border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] px-4 py-2 text-sm font-medium text-zinc-400 transition-all hover:border-[hsl(225,15%,25%)] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage >= totalPages}
              className="rounded-lg border border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] px-3 py-2 text-sm text-zinc-400 transition-all hover:border-[hsl(225,15%,25%)] hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Players;
